import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { ResponseWeeklyMemberReport, ResponseWeeklyTeamReport } from '../../../../../states/types/report'
import { secToHour, secToHourMinute, secToMinute } from '../../../../../utils/date'
import Loading from '../../../common/Loading'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import { ReactSVG } from 'react-svg'
import Tips from '../../../common/Tips'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void,
}

const WeeklyHeader: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    report,
    profile,
  } = useSelector((state: RootState) => (state))

  const {
    teams,
  } = useSelector((state: RootState) => (state.commonPart))

  const { t } = useTranslation()

  const [data, setData] = useState<ResponseWeeklyTeamReport | ResponseWeeklyMemberReport>()
  const [icon, setIcon] = useState<string>()

  const totalScore = useMemo(() => {
    if (!data || !data.scores) return 0
    let sum = 0
    data.scores.forEach((score) => {
      sum += score.current
    })
    return sum / 3
  }, [data])

  const stressScore = useMemo(() => {
    if (!data || !data.scores) return 0
    return data.scores.find((score) => score.label === 'stress_score')?.current || 0
  }, [data])

  const efficiencyScore = useMemo(() => {
    if (!data || !data.scores) return 0
    return data.scores.find((score) => score.label === 'efficiency_score')?.current || 0
  }, [data])

  const focusScore = useMemo(() => {
    if (!data || !data.scores) return 0
    return data.scores.find((score) => score.label === 'focus_score')?.current || 0
  }, [data])

  const buildTitle = useMemo(() => {
    if (data && "member" in data && data.member) {
      if (data.member.image_url) {
        setIcon(data.member.image_url)
      } else {
        setIcon(`/static/images/profile/${(data.member.id || 0) % 12}.jpeg`)
      }
      return `${data.member.name} さん`
    }
    if (data && "team" in data && data.team) {
      return `${data.team.name} チーム`
    }
    return ""
  }, [data])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.weeklyTeamReport) return
      setData(report.weeklyTeamReport)
    } else {
      if (!report.weeklyMemberReport) return
      setData(report.weeklyMemberReport)
    }
  }, [target, report.isLoading])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <section id="weekly_header" className="page_contents">
            <div className="content-block weekly">
              <div className="block-detail header_area">
                {
                  data && "member" in data && data.member && (
                    <div className="profile_area">
                      <div className="block">
                        <div className="name no_trans">
                          {buildTitle}
                        </div>
                        {
                          data.member?.applied_work_styles?.length ? (
                            <>
                              <div className="item">
                                <img
                                  alt=""
                                  src={`/static/images/work_style/${
                                    (data.member.applied_work_styles?.length && data.member.applied_work_styles[0].id) || 0
                                  }.png`}
                                  className="icon"
                                />
                                <div className="item_title">
                                  <div>働き方</div>
                                  {
                                    profile.work_style_list.find(
                                      (work_style) => work_style.id === (data.member?.applied_work_styles[0].id),
                                    )?.description
                                  }
                                </div>
                              </div>
                            </>
                          ) : (
                            <></>
                          )
                        }
                        {
                          data.member?.applied_occupation?.id ? (
                            <div className="item">
                              <img
                                alt=""
                                src={`/static/images/occupation/${data.member.applied_occupation?.id || 0}.png`}
                                className="icon"
                              />
                              <div className="item_title">
                                <div>職種</div>
                                {
                                  profile.occupationList.find(
                                    (occupation) => occupation.id === data.member?.applied_occupation?.id,
                                  )?.description
                                }
                              </div>
                            </div>
                          ) : (
                            <></>
                          )
                        }
                        {
                          data.member?.applied_work_styles?.length || data.member?.applied_occupation?.id ? (
                            <div className="item">
                              <div className="text_area">
                                <div>
                                  {secToHourMinute(data.member.base_work_time || 0)}
                                </div>
                              </div>
                              <div className="item_title">
                                <div>勤務時間</div>
                                {secToHourMinute(data.member.base_work_time || 0)}
                              </div>
                            </div>
                          ) : (
                            <></>
                          )
                        }
                      </div>
                    </div>
                  )
                }
                {
                  (data && "team" in data && data.team) && (
                    <div className="profile_area_team">
                      <div className="no_trans">
                        {buildTitle}
                      </div>
                    </div>
                  )
                }
                <div className="graph_area">
                  <div className="left_graph_item">
                    <div className="bg_border_center" />
                    <div className="bg_circle_center" />
                    <div className="bg_border_up_1" />
                    <div className="bg_border_up_2" />
                    <div className="bg_circle_up" />
                    <div className="bg_border_down_1" />
                    <div className="bg_border_down_2" />
                    <div className="bg_circle_down" />
                    <div
                      className="graph_wrap"
                      style={{
                        backgroundImage:
                          `radial-gradient(#FFFFFF 64%, transparent 0%),
                          conic-gradient(from 180deg, #4472BA 0% ${totalScore * 0.75}%, #FFFFFF ${totalScore * 0.75}% 100%)`,
                      }}
                    >
                      <div
                        className="needle"
                        style={{
                          transform: `rotate(${(totalScore * 2.7) + 90}deg)`,
                        }}
                      />
                      <div className="bg_shell" />
                      <div className="pie" />
                      <div className="ruler">
                        <div className="ruler_item">
                          Excellent
                        </div>
                        <div className="ruler_item">
                          Great
                        </div>
                        <div className="ruler_item">
                          Good
                        </div>
                        <div className="ruler_item">
                          Fair
                        </div>
                        <div className="inner_text">
                          {
                            target === "team" && data && "team" in data ? (
                              <>
                                <div className="team_icon no_trans">
                                  {teams?.find((team) => team.id === data?.team?.id)?.name.slice(0, 1)}
                                </div>
                              </>
                            ) : (
                              <div className="member_icon">
                                <img alt="" src={icon} />
                              </div>
                            )
                          }
                          <div className="text">
                            {data?.summary?.total_evaluation}
                          </div>
                          {
                            data?.summary?.total_evaluation === "Excellent" && (
                              <img
                                className="excellent_ribbon"
                                src="/static/images/bg/excellent_ribbon.png"
                                alt=""
                              />
                            )
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="right_graph_item">
                    <div
                      className={`mini_graph_item ${data?.scores?.find((score) => score.label === 'efficiency_score')?.checked && "checked"}`}
                    >
                      <div
                        className="graph_wrap"
                        style={{
                          backgroundImage:
                            `radial-gradient(#FFFFFF 64%, transparent 0%),
                            conic-gradient(from 180deg,
                              ${data?.scores?.find((score) => score.label === 'efficiency_score')?.checked ? "#FFA800" : "#4472BA"}
                              0% ${efficiencyScore * 0.75}%, #FFFFFF ${efficiencyScore * 0.75}% 100%)`,
                        }}
                      >
                        <div
                          className="needle"
                          style={{
                            transform: `rotate(${(efficiencyScore * 2.7) + 90}deg)`,
                          }}
                        />
                        <div className="pie" />
                        <div className="ruler">
                          <div className="ruler_item">
                            100
                          </div>
                          <div className="ruler_item">
                            75
                          </div>
                          <div className="ruler_item">
                            50
                          </div>
                          <div className="ruler_item">
                            25
                          </div>
                          <div className="ruler_item">
                            0
                          </div>
                          <div className="inner_text">
                            <div className="text">
                              業務効率適正度
                            </div>
                            <div className="point">
                              {efficiencyScore}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text_box">
                        {
                          data?.scores?.find((score) => score.label === 'efficiency_score')?.checked && (
                            <ReactSVG
                              src="/static/images/icon/icon-warn.svg"
                              className="icon_warn"
                            />
                          )
                        }
                        {
                          data?.comments?.efficiency_score ? (
                            <>
                              {
                                data?.comments?.efficiency_score.score_status ? (
                                  <>
                                    {
                                      t('report.weekly.summary_comment_firstl', {
                                        score_diff: data?.comments?.efficiency_score.score_diff > 0
                                          ? data?.comments?.efficiency_score.score_diff : '',
                                        score_status: t(`report.weekly.score_status.${data?.comments?.efficiency_score.score_status}`),
                                      })
                                    }
                                  </>
                                ) : (
                                  <></>
                                )
                              }
                              {
                                t('report.weekly.summary_comment_second', {
                                  score_short_prefix: t(`report.weekly.score_short_prefix.efficiency_score`),
                                })
                              }
                              <b>
                                {
                                  t(`report.indicator.${data?.comments?.efficiency_score?.indicator_name}`)
                                }
                              </b>
                              {
                                t('report.weekly.summary_comment_last', {
                                  indicator_name: t(`report.indicator.${data?.comments?.efficiency_score?.indicator_name}`),
                                })
                              }
                            </>
                          ) : (
                            <>
                              {
                                t('report.weekly.no_summary_comment')
                              }
                            </>
                          )
                        }
                        <AnchorLink
                          offset="200"
                          data-gtm-click="業務効率適正度 - anchor_link_button"
                          href="#efficiency"
                          className="link_button"
                        >
                          詳細を見る
                        </AnchorLink>
                      </div>
                    </div>
                    <div
                      className={`mini_graph_item ${data?.scores?.find((score) => score.label === 'stress_score')?.checked && "checked"}`}
                    >
                      <div
                        className="graph_wrap"
                        style={{
                          backgroundImage:
                            `radial-gradient(#FFFFFF 64%, transparent 0%),
                            conic-gradient(from 180deg,
                              ${data?.scores?.find((score) => score.label === 'stress_score')?.checked ? "#FFA800" : "#4472BA"}
                              0% ${stressScore * 0.75}%, #FFFFFF ${stressScore * 0.75}% 100%)`,
                        }}
                      >
                        <div
                          className="needle"
                          style={{
                            transform: `rotate(${(stressScore * 2.7) + 90}deg)`,
                          }}
                        />
                        <div className="pie" />
                        <div className="ruler">
                          <div className="ruler_item">
                            100
                          </div>
                          <div className="ruler_item">
                            75
                          </div>
                          <div className="ruler_item">
                            50
                          </div>
                          <div className="ruler_item">
                            25
                          </div>
                          <div className="ruler_item">
                            0
                          </div>
                          <div className="inner_text">
                            <div className="text">
                              業務負荷適正度
                            </div>
                            <div className="point">
                              {stressScore}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text_box">
                        {
                          data?.scores?.find((score) => score.label === 'stress_score')?.checked && (
                            <ReactSVG
                              src="/static/images/icon/icon-warn.svg"
                              className="icon_warn"
                            />
                          )
                        }
                        {
                          data?.comments?.stress_score ? (
                            <>
                              {
                                data?.comments?.stress_score.score_status ? (
                                  <>
                                    {
                                      t('report.weekly.summary_comment_firstl', {
                                        score_diff: data?.comments?.stress_score.score_diff > 0 ? data?.comments?.stress_score.score_diff : '',
                                        score_status: t(`report.weekly.score_status.${data?.comments?.stress_score.score_status}`),
                                      })
                                    }
                                  </>
                                ) : (
                                  <></>
                                )
                              }
                              {
                                t('report.weekly.summary_comment_second', {
                                  score_short_prefix: t(`report.weekly.score_short_prefix.stress_score`),
                                })
                              }
                              <b>
                                {
                                  t(`report.indicator.${data?.comments?.stress_score?.indicator_name}`)
                                }
                              </b>
                              {
                                t('report.weekly.summary_comment_last', {
                                  indicator_name: t(`report.indicator.${data?.comments?.stress_score?.indicator_name}`),
                                })
                              }
                            </>
                          ) : (
                            <>
                              {
                                t('report.weekly.no_summary_comment')
                              }
                            </>
                          )
                        }
                        <AnchorLink
                          offset="200"
                          data-gtm-click="業務負荷適正度 - anchor_link_button"
                          href="#stress"
                          className="link_button"
                        >
                          詳細を見る
                        </AnchorLink>
                      </div>
                    </div>
                    <div
                      className={`mini_graph_item ${data?.scores?.find((score) => score.label === 'focus_score')?.checked && "checked"}`}
                    >
                      <div
                        className="graph_wrap"
                        style={{
                          backgroundImage:
                            `radial-gradient(#FFFFFF 64%, transparent 0%),
                            conic-gradient(from 180deg,
                              ${data?.scores?.find((score) => score.label === 'focus_score')?.checked ? "#FFA800" : "#4472BA"}
                              0% ${focusScore * 0.75}%, #FFFFFF ${focusScore * 0.75}% 100%)`,
                        }}
                      >
                        <div
                          className="needle"
                          style={{
                            transform: `rotate(${(focusScore * 2.7) + 90}deg)`,
                          }}
                        />
                        <div className="pie" />
                        <div className="ruler">
                          <div className="ruler_item">
                            100
                          </div>
                          <div className="ruler_item">
                            75
                          </div>
                          <div className="ruler_item">
                            50
                          </div>
                          <div className="ruler_item">
                            25
                          </div>
                          <div className="ruler_item">
                            0
                          </div>
                          <div className="inner_text">
                            <div className="text">
                              業務集中適正度
                            </div>
                            <div className="point">
                              {focusScore}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text_box">
                        {
                          data?.scores?.find((score) => score.label === 'focus_score')?.checked && (
                            <ReactSVG
                              src="/static/images/icon/icon-warn.svg"
                              className="icon_warn"
                            />
                          )
                        }
                        {
                          data?.comments?.focus_score ? (
                            <>
                              {
                                data?.comments?.focus_score.score_status ? (
                                  <>
                                    {
                                      t('report.weekly.summary_comment_firstl', {
                                        score_diff: data?.comments?.focus_score.score_diff > 0 ? data?.comments?.focus_score.score_diff : '',
                                        score_status: t(`report.weekly.score_status.${data?.comments?.focus_score.score_status}`),
                                      })
                                    }
                                  </>
                                ) : (
                                  <></>
                                )
                              }
                              {
                                t('report.weekly.summary_comment_second', {
                                  score_short_prefix: t(`report.weekly.score_short_prefix.focus_score`),
                                })
                              }
                              <b>
                                {
                                  t(`report.indicator.${data?.comments?.focus_score?.indicator_name}`)
                                }
                              </b>
                              {
                                t('report.weekly.summary_comment_last', {
                                  indicator_name: t(`report.indicator.${data?.comments?.focus_score?.indicator_name}`),
                                })
                              }
                            </>
                          ) : (
                            <>
                              {
                                t('report.weekly.no_summary_comment')
                              }
                            </>
                          )
                        }
                        <AnchorLink
                          offset="200"
                          data-gtm-click="業務集中適正度 - anchor_link_button"
                          href="#focus"
                          className="link_button"
                        >
                          詳細を見る
                        </AnchorLink>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="down_area">
                  <div className={`detail_block ${data?.summary?.average.work_time.checked && 'checked'}`}>
                    {
                      data?.summary?.average.work_time.checked && (
                        <ReactSVG
                          src="/static/images/icon/icon-warn.svg"
                          className="icon_warn"
                        />
                      )
                    }
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        minWidth: "120px",
                      }}
                    >
                      <Tips
                        description={t(`report.indicator_help.weekly.${target}.summary.average_work_time`)}
                        className="detail_title"
                      >
                        <>
                          平均勤務時間
                        </>
                      </Tips>
                      <div className="detail_value">
                        {
                          secToHour(data?.summary?.average.work_time.value || 0) && (
                            <>
                              <span>
                                {secToHour(data?.summary?.average.work_time.value || 0)}
                              </span>
                              時間
                            </>
                          )
                        }
                        {
                          secToMinute(data?.summary?.average.work_time.value || 0) && (
                            <>
                              <span>
                                {secToMinute(data?.summary?.average.work_time.value || 0)}
                              </span>
                              分
                            </>
                          )
                        }
                      </div>
                    </div>
                    <div className="icon_area">
                      {
                        data?.is_past_data_exist
                        && Math.abs(data?.summary?.average.work_time.increased_value || 0) >= 60
                        && (data?.summary?.average.work_time.increased_value || 0) > 0
                        && (
                          <ReactSVG
                            src="/static/images/icon/icon-up.svg"
                            className="icon up"
                          />
                        )
                      }
                      {
                        data?.is_past_data_exist
                        && Math.abs(data?.summary?.average.work_time.increased_value || 0) >= 60
                        && (data?.summary?.average.work_time.increased_value || 0) < 0
                        && (
                          <ReactSVG
                            src="/static/images/icon/icon-down.svg"
                            className="icon down"
                          />
                        )
                      }
                    </div>
                    <button
                      data-gtm-click="平均勤務時間 - header_area"
                      type="button"
                      onClick={
                        () => heatmapModalOpenCallback({
                          type: "hour",
                          range: "monthly",
                          target: target,
                          id: 1,
                          title: "work_time",
                          legend: "勤務時間(h)",
                          startColor: "#ECF0F5",
                          endColor: "#008FFB",
                          minimum: 5000,
                          displaySearchLink: true,
                          name: "",
                        })
                      }
                    >
                      比較を見る
                    </button>
                  </div>
                  <div className={`detail_block ${data?.summary?.average.active_time.checked && 'checked'}`}>
                    {
                      data?.summary?.average.active_time.checked && (
                        <ReactSVG
                          src="/static/images/icon/icon-warn.svg"
                          className="icon_warn"
                        />
                      )
                    }
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        minWidth: "120px",
                      }}
                    >
                      <Tips
                        description={t(`report.indicator_help.weekly.${target}.summary.average_active_time`)}
                        className="detail_title"
                      >
                        <>
                          平均作業時間
                        </>
                      </Tips>
                      <div className="detail_value">
                        {
                          secToHour(data?.summary?.average.active_time.value || 0) && (
                            <>
                              <span>
                                {secToHour(data?.summary?.average.active_time.value || 0)}
                              </span>
                              時間
                            </>
                          )
                        }
                        {
                          secToMinute(data?.summary?.average.active_time.value || 0) && (
                            <>
                              <span>
                                {secToMinute(data?.summary?.average.active_time.value || 0)}
                              </span>
                              分
                            </>
                          )
                        }
                      </div>
                    </div>
                    <div className="icon_area">
                      {
                        data?.is_past_data_exist
                        && Math.abs(data?.summary?.average.active_time.increased_value || 0) >= 60
                        && (data?.summary?.average.active_time.increased_value || 0) > 0
                        && (
                          <ReactSVG
                            src="/static/images/icon/icon-up.svg"
                            className="icon up"
                          />
                        )
                      }
                      {
                        data?.is_past_data_exist
                        && Math.abs(data?.summary?.average.active_time.increased_value || 0) >= 60
                        && (data?.summary?.average.active_time.increased_value || 0) < 0
                        && (
                          <ReactSVG
                            src="/static/images/icon/icon-down.svg"
                            className="icon down"
                          />
                        )
                      }
                    </div>
                    <button
                      data-gtm-click="平均作業時間 - header_area"
                      type="button"
                      onClick={
                        () => heatmapModalOpenCallback({
                          type: "hour",
                          range: "monthly",
                          target: target,
                          id: 1,
                          title: "active_time",
                          legend: "作業時間(h)",
                          startColor: "#ECF0F5",
                          endColor: "#008FFB",
                          minimum: 0,
                          displaySearchLink: true,
                          name: "",
                        })
                      }
                    >
                      比較を見る
                    </button>
                  </div>
                  <div className={`detail_block ${data?.summary?.average.biz_point.checked && 'checked'}`}>
                    {
                      data?.summary?.average.biz_point.checked && (
                        <ReactSVG
                          src="/static/images/icon/icon-warn.svg"
                          className="icon_warn"
                        />
                      )
                    }
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        minWidth: "120px",
                      }}
                    >
                      <Tips
                        description={t(`report.indicator_help.weekly.${target}.summary.average_biz_point`)}
                        className="detail_title"
                      >
                        <>
                          平均操作量
                        </>
                      </Tips>
                      <div className="detail_value">
                        <span>
                          {data?.summary?.average.biz_point.value.toLocaleString() || 0}
                        </span>
                        Biz
                      </div>
                    </div>
                    <div className="icon_area">
                      {
                        data?.is_past_data_exist
                        && Math.abs(data?.summary?.average.biz_point.increased_value || 0) >= 500
                        && (data?.summary?.average.biz_point.increased_value || 0) > 0
                        && (
                          <ReactSVG
                            src="/static/images/icon/icon-up.svg"
                            className="icon up"
                          />
                        )
                      }
                      {
                        data?.is_past_data_exist
                        && Math.abs(data?.summary?.average.biz_point.increased_value || 0) >= 500
                        && (data?.summary?.average.biz_point.increased_value || 0) < 0
                        && (
                          <ReactSVG
                            src="/static/images/icon/icon-down.svg"
                            className="icon down"
                          />
                        )
                      }
                    </div>
                    <button
                      data-gtm-click="平均操作量 - header_area"
                      type="button"
                      onClick={
                        () => heatmapModalOpenCallback({
                          type: "biz",
                          range: "monthly",
                          target: target,
                          id: 1,
                          title: "biz_point",
                          legend: "操作量(biz)",
                          startColor: "#ECF0F5",
                          endColor: "#008FFB",
                          minimum: 0,
                          displaySearchLink: true,
                          name: "",
                        })
                      }
                    >
                      比較を見る
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )
      }
    </>
  )
}

export default WeeklyHeader
